// 부트스트랩: 게임(고정 60Hz) + 입력 + 렌더 + UI를 연결한다.
import { createGame } from './game/game';
import { InputCollector } from './core/input';
import { FixedLoop, runLoop } from './core/loop';
import { FIXED_DT } from './shared/config';
import { compatModeRequested, createRenderer } from './render/index';
import { createUI } from './ui/index';
import { assetStatus } from './render/assets';
import { bindIntro, introAbort, introDone, introEnabled, introVeil } from './ui/intro';
import { createAudio } from './audio/index';
import { ScreenReserve } from './ui/screenReserve';
import { showStartupError } from './ui/startupError';
import { RENDERER_SESSION_KEY } from './render/quality';
import { cameraYaw, screenToWorld } from './shared/cameraYaw';
import { Scene, combatScene } from './shared/types';
import { startInAppEscape } from './ui/inAppBanner';
import { accountClient, applyStagedRestore, startAccount } from './account/index';
import { setBusyVeil } from './account/busyVeil';
import { bootCharacters, charactersEnabled, hasLegacyProgress } from './characters/index';
import { NAME_NOTICE, mountCharacterEntry, startCharacterSelect } from './ui/characterEntry';
import { startErrorCollector } from './telemetry/collector';
import { startCommunity, type SceneName } from './community/index';
import { startPwa } from './pwa/register';
import { startRegionData, prioritizeRegion, regionOfStage } from './pwa/regionData';
import { startPwaBand } from './pwa/band';
import { REGION_IDS, type RegionId } from './shared/levels';
import { FieldNetLink } from './game/fieldNetLink';
import { FieldNetHud } from './ui/fieldNet';
import { previewQueryAllowedHere } from './shared/previewFlags';
import { PartyClient } from './game/partyNet';
import { RaidClient } from './game/raidNet';
import { RaidLedgerSync } from './game/raidLedger';
import { raidNetEnabledHere, raidWsOverride } from './shared/content/raidNetFlags';
import { setRaidNetPanel } from './ui/raidPanel';
import { applyMail, type MailLike } from './game/mailApply';
import { marketEnabledHere, partyEnabledHere, partyWsOverride } from './shared/content/social';
import { mountPartyUi } from './ui/partyPanel';
import { mountMailUi } from './ui/mailPanel';
import { mountMarketUi } from './ui/marketPanel';
import { MarketClient } from './market/marketClient';
import { marketLedger } from './game/marketApply';
import { activeCharacterId } from './characters/scope';
import { MailClient } from './market/mailClient';
import { learnedMaskOf, pageSwitchOpen } from './ui/slotsModel';
import { growthOf } from './ui/skillTreeModel';

/** 파티 명단 이름(계정 이름이 붙기 전까지 영웅 이름) */
const PARTY_HERO_NAME: Readonly<Record<string, string>> = { kael: '카엘', lyra: '라이라', seren: '세렌', brann: '브란', lumen: '루멘' };

// 오류 수집(D5b): 부팅 맨 처음(게임이 만들어지기 전 오류도 잡는다). 장면은 게임이 생기면 바뀐다
let sceneName: () => SceneName = () => 'boot';
startErrorCollector(() => sceneName());
// 인앱 브라우저(카카오톡·라인 등)는 무거운 부팅 전에 외부 브라우저로 넘기기를 시작한다(c014). 넘기지 못해도 아래 부팅은 그대로 이어진다
startInAppEscape();

/** 마을+Kael이 준비된 뒤 R1 던전 자원의 뒤쪽 준비를 인트로가 기다리는 최대 시간 */
const INTRO_WARM_WAIT_MS = 6000;

let stopped = false;
/** MMO 공유 필드 연결·HUD(게임을 만든 뒤 채운다) */
let syncFieldNet = (): void => {};
/** 시작을 막는 실패(GPU 없음·장치 유실 반복·부팅 예외): 플레이 화면을 멈추고 안내 화면만 남긴다. */
function fail(err: unknown): void {
  console.error(err);
  stopped = true;
  introAbort();
  showStartupError(document, err, { sessionKey: RENDERER_SESSION_KEY, compatActive: compatModeRequested(new URLSearchParams(location.search).get('renderer') === 'webgl') });
}

async function boot() {
  const params = new URLSearchParams(location.search);
  // 인트로(CTA·프롤로그·로딩 화면)는 index.html에서 이미 떠 있다. 준비 상태를 넘기고, 준비 전 부팅은 마을 먼저 단계 순서로 한다.
  const staged = introEnabled() && params.get('warm') !== 'boot';
  // 인트로 게이트: 마을+Kael 준비(assetStatus.ready)에 더해 R1 던전 자원의 뒤쪽 준비까지 끝나야 걷는다(그 구간은 메인 스레드를 몇 초 잡아 마을에서 돌면 끊긴다).
  // 뒤쪽 준비가 멈춰도 INTRO_WARM_WAIT_MS 뒤에는 걷는다. 예전 20초는 모바일 데이터(인스타 인앱 등)에서 막대가 70%대에 오래 멈춰 보였다
  // (대표님 2026-10-23 보고). 남은 준비는 타이틀 뒤에서 이어지고, 덜 된 채 던전에 들어가면 대기 화면(veil)이 받는다.
  let rendererRef: { warmProgress(): number } | null = null;
  let readySince = 0;
  const settled = (): boolean => {
    if (!rendererRef || rendererRef.warmProgress() >= 1) return true;
    if (!readySince) readySince = performance.now();
    return performance.now() - readySince > INTRO_WARM_WAIT_MS;
  };
  bindIntro({
    get ready() { return assetStatus.ready && settled(); },
    get failed() { return assetStatus.failed && settled(); },
    get progress() { return Math.min(0.99, assetStatus.progress * 0.75 + (rendererRef ? rendererRef.warmProgress() : 0) * 0.25); },
  });
  const canvas = document.getElementById('game') as HTMLCanvasElement;
  const uiRoot = document.getElementById('ui') as HTMLElement;
  // 계정(D5a): 다른 기기에서 당겨온 서버 진행이 대기 중이면 저장 모듈이 키를 읽기 전에 적용한다(로컬 사본을 먼저 남긴다)
  const accountRestore = applyStagedRestore();
  // 캐릭터(docs/CHARACTER-PRD.md §13): 저장 모듈이 키를 읽기 전에 지금 캐릭터를 정한다(처음이면 기존 진행 이관). 꺼져 있으면 아무것도 안 한다(원래 키)
  const chars = bootCharacters({ wiped: accountRestore.applied && 'wiped' in accountRestore });
  // 지금 캐릭터가 없으면(처음·삭제·받는 중) 게임을 만들지 않고 선택·생성 화면만(고르면 새로고침 — src/ui/characterEntry.ts)
  // 새 기기(원래 진행 없음)는 이관 판정 전에도 게임을 만들지 않고 「준비 중」 화면 → 판정 뒤 선택·생성(서버에 닿지 않으면 「서버 없이 시작」만 원래 키로)
  const firstRun = chars.mode === 'legacy' && chars.reason === 'migration-pending' && charactersEnabled() && !hasLegacyProgress(window.localStorage);
  if (chars.mode === 'select' || firstRun) {
    introAbort();
    await startCharacterSelect(uiRoot, startAccount, { setBusy: setBusyVeil, preparing: firstRun });
  }
  const game = createGame(Number(params.get('seed') ?? 1) || 1);
  // 캐릭터 모드: 타이틀·마을 HUD의 「캐릭터」가 여는 선택 화면(고르면 저장 쓰고 새로고침)
  const charEntry = chars.mode === 'active' ? mountCharacterEntry(uiRoot, { flush: () => { game.saveRunProgress(); game.flushSaves(); }, reload: () => location.reload() }) : null;
  const SCENE_NAMES: SceneName[] = ['title', 'village', 'dungeon', 'result', 'defeat', 'dungeon', 'dungeon']; // 5 필드·6 균열은 전투 장면으로 센다
  sceneName = () => SCENE_NAMES[game.view.flow.scene] ?? 'boot';
  const input = new InputCollector();
  input.attach(window);
  // 스킬 슬롯 키(D1e): 1~6 현재 페이지 · Alt+1~6 다른 페이지 · Tab 페이지 전환(던전·일시정지 아님일 때만 가로챈다). 슬롯 → 스킬은 view.slots가 정한다
  input.setSlotKeys({
    view: game.view.slots,
    // 2쪽이 비어 있으면(1-1 등) Tab 전환도 하지 않는다(RG-018, 칩·서랍 단추와 같은 pageSwitchOpen)
    togglePage: () => { if (pageSwitchOpen(game.view.slots, learnedMaskOf(growthOf(game.view)))) game.command({ type: 'setSkillPage' }); },
    tabActive: () => combatScene(game.view.flow.scene) && !game.view.flow.paused,
  });
  const q = params.get('quality');
  // HUD가 차지한 화면 영역의 단일 소스: HUD가 쓰고 피해 숫자(렌더러)가 읽는다
  const reserve = new ScreenReserve();
  const renderer = await createRenderer(canvas, game.view, {
    forceWebGL: params.get('renderer') === 'webgl',
    quality: q === 'low' || q === 'mid' || q === 'high' ? q : 'auto',
    reserve,
    staged,
    onFatal: (err) => {
      game.command({ type: 'setPaused', paused: true });
      fail(err);
    },
  });
  rendererRef = renderer;
  // 자산 패치·PWA: 입장 명령이 오면 그 지역 데이터를 가장 먼저 받는다(이미 받아 뒀으면 아무 일도 없다). 게임 명령은 그대로 전달한다
  const ui = createUI(uiRoot, game.view, input, (c) => {
    if (c.type === 'enterStage' || c.type === 'enterDungeon') { const r = c.type === 'enterStage' ? regionOfStage(c.stage) : (c.region ?? regionOfStage(c.stage)); if (r) prioritizeRegion(r); }
    game.command(c);
  }, { backend: renderer.backend, reserve });
  // 오디오: 게임 이벤트를 자기 커서로 읽는다(?qa·개발 서버에서는 window.__DHA__ QA 훅)
  // 인트로가 떠 있는 동안은 소리를 낼 수 없으므로(입력을 삼킨다) BGM·효과음(약 2.7MB) 내려받기를 인트로가 걷힌 뒤로 미뤄 첫 화면 자원에 회선을 준다
  const audio = createAudio(game.view, { qa: params.has('qa') || Boolean(import.meta.env.DEV), deferNetwork: introDone() });
  // 던전 자원이 아직 준비 중일 때 들어가면 같은 로딩 화면을 잠깐 다시 연다(그레이박스·빠진 몹을 보이지 않는다). 최대 25초.
  let veilSince = 0, veilOn = false, pausedByVeil = false;
  const updateVeil = (): void => {
    let wait = renderer.waiting();
    if (wait) {
      if (!veilSince) veilSince = performance.now();
      if (performance.now() - veilSince > 25000) wait = null;
    } else veilSince = 0;
    if (wait && !veilOn) {
      veilOn = true;
      introVeil(wait);
      if (!game.view.flow.paused) { game.command({ type: 'setPaused', paused: true }); pausedByVeil = true; }
    } else if (!wait && veilOn) {
      veilOn = false;
      introVeil(null);
      if (pausedByVeil) { game.command({ type: 'setPaused', paused: false }); pausedByVeil = false; }
    }
  };
  // 새 버전 알림 띠: 마을에서만(다른 창이 열려 있지 않을 때). 전투 중에는 나타나지 않는다
  const pwaBand = startPwaBand(uiRoot, {
    visible: () => { const v = game.view; const vv = v.village; return v.flow.scene === Scene.Village && !v.flow.paused && vv.menuNpc < 0 && !vv.inventoryOpen && !vv.stageMapOpen && !vv.questOpen; },
  });
  const loop = new FixedLoop({
    // 안내 화면이 뜨면 시뮬레이션도 멈춘다(입력으로 일시정지가 풀려 보이지 않는 전투가 진행되지 않게)
    // 카메라가 돌면 화면 축 입력을 월드 축으로 돌린다(시뮬레이션·리플레이는 월드 축만 본다)
    step: () => {
      if (stopped) return;
      const f = input.sample();
      screenToWorld(f, cameraYaw.rad);
      game.step(FIXED_DT, f);
    },
    render: (alpha, dt) => {
      if (stopped) return;
      input.pollFrame();
      game.flushSaves(); // 고정 스텝에서 미룬 스킬 슬롯 저장을 프레임 단위로 합쳐 쓴다
      updateVeil();
      // 같은 프레임 HUD 예약을 피해 숫자 배치 전에 확정한다.
      syncFieldNet();
      // 파티 판(레인 D): 동료 영웅 보간 구간을 보기로 옮긴다(시뮬은 고정 스텝 game.party.step이 한다)
      game.party.frame();
      loop.catchUp = game.party.active || game.raidNet.active; // 파티 판: 느린 기기에서도 내 시뮬이 서버 실시간을 따라가게(core/loop.ts)
      ui.update(dt);
      pwaBand.update();
      renderer.render(alpha, dt);
      audio.update(dt);
    },
  });
  window.addEventListener('resize', () => renderer.resize());
  // 화면이 가려지면 일시정지(복귀 시 밀린 시간은 FixedLoop가 버린다)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') game.command({ type: 'setPaused', paused: true });
  });
  // 판 도중 탭을 닫거나 숨기면 이번 판 EXP를 지금까지 기록한다(성장 v1). 같은 EXP는 두 번 지급되지 않는다
  const saveRunProgress = (): void => { game.saveRunProgress(); game.flushSaves(); };
  window.addEventListener('pagehide', saveRunProgress);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') saveRunProgress();
  });
  // 다른 탭에서 스테이지를 깼으면 마을에 서 있던 이 탭도 돌아올 때 새 지역 문·워프 칸을 보인다(c018, PR #160 리뷰 P2)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') game.refreshProgress();
  });
  window.addEventListener('focus', () => game.refreshProgress());
  // 자유 입장(지도 그대로·해금 무시)은 ?qa·개발 서버에서만. 실제 빌드는 지역 입장도 고른 스테이지·해금을 따른다(D3a)
  game.allowFreeEntry = params.has('qa') || Boolean(import.meta.env.DEV);
  // 세계 법칙(C10b): 실제 빌드는 날짜로 정한 이번 주 규칙을 던전 판에 건다. 시험 하네스는 기본 꺼짐(날짜에 따라 기존 수치가 흔들리지 않게)
  game.worldRulesLive = true;
  // 환생 모드 확인(레인 R): ?modes=1이면 미공개 모드(ARPG·MMO)도 열리고 윤회의 문이 퀘스트 없이 Lv99만으로 열린다(운영 공개 스위치 REBIRTH[].released는 그대로)
  // 운영 호스트(dghero·dungeonhero)에서는 무시한다 — 출시 전 모드는 REBIRTH[].released로만 연다(previewFlags)
  const previewOk = previewQueryAllowedHere();
  game.rebirth.modes = previewOk && params.get('modes') === '1';
  // MMO 공유 필드(레인 M 통합): 2차 환생(MMORPG 모드) 영웅이 필드에 들어가면 연결한다. ?mmo=1(?qa·개발 전용)은 모드와 상관없이, ?fieldws=는 QA 서버 주소
  const qaOrDev = previewOk && (params.has('qa') || Boolean(import.meta.env.DEV));
  const fieldLink = new FieldNetLink(game, { force: qaOrDev && params.get('mmo') === '1', url: qaOrDev ? params.get('fieldws') ?? undefined : undefined });
  const fieldHudWrap = document.createElement('div');
  fieldHudWrap.style.cssText = 'position:absolute;inset:0;pointer-events:none;display:none;';
  uiRoot.appendChild(fieldHudWrap);
  const fieldHud = new FieldNetHud(fieldHudWrap);
  let fieldHudAt = 0;
  syncFieldNet = (): void => {
    fieldLink.sync();
    const net = fieldLink.net;
    fieldHudWrap.style.display = net ? '' : 'none';
    const now = performance.now();
    if (net && now - fieldHudAt > 100) { fieldHudAt = now; fieldHud.update(net, Date.now(), now); }
  };
  // 파티 던전·우편함(레인 D, docs/PARTY-TRADE-PRD.md): 공개 스위치(PARTY_RELEASE) 또는 미리보기 ?party=1(운영 호스트는 무시)에서만.
  // 연결층(PartyClient) 하나를 게임 연결점(game.party)과 파티 창이 같이 쓴다. 이름·영웅·레벨은 접속(hello) 때마다 지금 값을 읽는다
  if (partyEnabledHere()) {
    const heroName = (): string => PARTY_HERO_NAME[game.view.heroId ?? 'kael'] ?? '모험가';
    const partyClient = new PartyClient({
      get name() { return heroName(); },
      get hero() { return game.view.heroId ?? 'kael'; },
      get lv() { return game.growth.level; },
      url: partyWsOverride(),
    });
    game.party.attach(partyClient);
    mountPartyUi({ client: partyClient, heroName, inVillage: () => game.view.flow.scene === Scene.Village });
  }
  // 레이드 4인 넷 모드(docs/RAID-PRD.md 0장 2단계): 공개 스위치(RAID_NET_RELEASE) 또는 미리보기 ?raidnet=1에서만. 연결층 하나를 게임 연결점(game.raidNet)과 지휘소 창 「같이 출정」이 같이 쓴다.
  // 우편함(보상 수령)은 파티와 같은 창이라, 레이드 넷이 열린 곳에서도 우편함을 붙인다(아래 조건)
  // 입장 직전 서버 주 장부 맞추기(RaidClient.beforeConnect) — 아래 레이드 장부 블록이 채운다. 겹치지 않은 솔로 영수증을 먼저 올려 같은 n의 이중 지급 창을 닫는다
  let raidLedger: RaidLedgerSync | null = null;
  const beforeRaidConnect = (): Promise<void> => (raidLedger ? raidLedger.sync(true) : Promise.resolve());
  if (raidNetEnabledHere()) {
    const raidClient = new RaidClient({
      beforeConnect: beforeRaidConnect,
      get name() { return PARTY_HERO_NAME[game.view.heroId ?? 'kael'] ?? '모험가'; },
      get hero() { return game.view.heroId ?? 'kael'; },
      get lv() { return game.growth.level; },
      url: raidWsOverride(),
      characterId: () => activeCharacterId() ?? 'legacy',
    });
    game.raidNet.attach(raidClient);
    setRaidNetPanel(raidClient);
  }
  // 레이드 주간 장부 통합(C1a, game/raidLedger.ts): 레이드 넷이 열린 곳에서만 — 솔로 영수증을 서버 장부에 올리고 서버 장부(넷 판 포함)를 받아 같은 주 3회로 센다.
  // 레이드 넷이 꺼진 곳(운영 기본)에서는 붙이지 않는다 = 솔로는 예전처럼 로컬 영수증만. 부트 3초 뒤 한 번, 레이드 결과가 나오면 바로, 그 밖에는 2분마다
  if (raidNetEnabledHere()) {
    const ledger = raidLedger = new RaidLedgerSync(game, { characterId: () => activeCharacterId() ?? 'legacy' });
    let seenResult = '';
    setTimeout(() => { void ledger.sync(true); }, 3000);
    // 로그인·구글 연동·계정 바뀜: 연동 없음으로 쉬던 시간(5분)을 풀고 바로 강제 sync — 방금 연동한 사람이 장부 없이 넷에 들어가 이중 지급되는 창을 닫는다
    let acctKey: string | null = null;
    accountClient().onChange((st) => {
      const key = st.provider + ':' + (st.accountId ?? '');
      const prev = acctKey;
      acctKey = key;
      if (prev !== null && prev !== key && st.accountId) void ledger.wake();
    });
    setInterval(() => {
      const res = game.view.result;
      if (res.raid && res.resultId && res.resultId !== seenResult) { seenResult = res.resultId; void ledger.sync(true); return; }
      if (Date.now() - ledger.syncedAt > 120_000) void ledger.sync();
    }, 2000);
  }
  // 우편함: 1단계는 파티 보상만(PARTY_NET), 2단계 거래소(MARKET_NET)도 같은 우편함. 받기 → 장비 장부 적용(저장 확정) → ack
  if (partyEnabledHere() || marketEnabledHere() || raidNetEnabledHere()) {
    const mailUi = mountMailUi({ mail: new MailClient({ characterId: () => activeCharacterId() ?? 'legacy' }), apply: (m) => applyMail(game, m), inVillage: () => game.view.flow.scene === Scene.Village });
    // 거래소(2단계, MARKET_RELEASE 또는 ?market=1): 거래 금고·보관함·경매. 장부 연결은 marketLedger(mkOut·mkGold)
    if (marketEnabledHere()) {
      const ledger = marketLedger(game);
      mountMarketUi({ market: new MarketClient(ledger, undefined, () => activeCharacterId() ?? 'legacy'), ledger, inVillage: () => game.view.flow.scene === Scene.Village, openMail: () => mailUi.open() });
    }
  }
  // QA 픽스처: ?start=village|r1|r2 는 타이틀을 건너뛴다(정상 흐름과 구분해 기록한다).
  // 스페셜 몬스터 강제 출현(?special=goldGoblin|champion): ?qa·개발 서버 전용. 스테이지 판의 일반 스테이지에서만 먹는다(보스판은 그대로 없음)
  const forceSpecial = params.get('special');
  if ((params.has('qa') || import.meta.env.DEV) && (forceSpecial === 'goldGoblin' || forceSpecial === 'champion')) game.special.force = forceSpecial;
  const start = params.get('start');
  if (start === 'village') game.command({ type: 'start' });
  if (start === 'r1') game.command({ type: 'enterDungeon' });
  if (start === 'r2') game.command({ type: 'enterDungeon', region: 'r2' });
  // D3a: ?start=r3-s05 꼴은 그 스테이지로 입장(열린 스테이지만 — 진행은 저장소 그대로, 플레이어 명령과 같은 경로). D3b 하드 h3-s05·C10 헬 x3-s05도 같다
  if (start && /^[rhx]\d-s\d\d$/.test(start)) game.command({ type: 'enterStage', stage: start });
  // QA 훅이 연결층을 만들 때(지휘소 창이 없어도): 이미 붙어 있으면 그것을 쓴다
  const raidClientOf = (): RaidClient => {
    let c = game.raidNet.client;
    if (!c) {
      c = new RaidClient({
        beforeConnect: beforeRaidConnect,
        get name() { return PARTY_HERO_NAME[game.view.heroId ?? 'kael'] ?? '모험가'; },
        get hero() { return game.view.heroId ?? 'kael'; },
        get lv() { return game.growth.level; },
        url: raidWsOverride(),
        characterId: () => activeCharacterId() ?? 'legacy',
      });
      game.raidNet.attach(c);
    }
    return c;
  };
  if (params.has('qa') || import.meta.env.DEV) {
    (window as unknown as { __DH__: unknown }).__DH__ = {
      get kael() { return renderer.kael; },
      view: game.view,
      loop,
      backend: renderer.backend,
      backendWhy: renderer.choice.why,
      quality: renderer.quality,
      drawCalls: renderer.drawCalls,
      perf: () => renderer.perf(),
      flashes: () => renderer.flashes(),
      damageNumbers: () => renderer.damageNumbers(),
      // 화면 기반 플레이 증거가 픽스처 명령을 쓰지 않았음을 대조하는 읽기 전용 값
      qaOps: () => game.qaOps,
      // 입력 수집기 상태 사본(읽기 전용 진단, first30-mobile-polish)
      input: () => input.debugState(),
      // C8-6 레이드 룩 고정 프레임(game/qaFrame.ts RAID_FRAMES 이름, null = 끔). 픽스처 — qaOps에 센다
      raidFrame: (name: string | null) => game.setQaFrame(name),
      // C1a 레이드 주간 장부(읽기 전용 진단 + 즉시 맞추기). 레이드 넷이 꺼진 곳이면 null
      raidLedger: () => raidLedger ? { last: raidLedger.last, syncedAt: raidLedger.syncedAt, server: game.raidServer, pending: raidLedger.pending() } : null,
      raidLedgerSync: () => raidLedger ? raidLedger.wake() : Promise.resolve(),
      // QA 픽스처 명령(정상 플레이 증거와 분리해 기록한다)
      qa: (op: 'fillAwaken' | 'gotoRoom' | 'spawnWave' | 'killAll' | 'bossLow', arg = 0) => game.command({ type: 'qa', op, arg }),
      // 동행 펫 줍기 훅(D2a, 펫 시스템 전): id(예: 'pet_moss')면 그 펫 반경으로 자동 줍기, null이면 끈다. 픽스처 — qaOps에 센다
      pet: (id: string | null) => { game.loot.setPet(id); game.qaOps++; },
      // 이름 있는 유니크 드롭 주입(l1029 QA): 자연 드롭 확률이 1~3%라 실제 처치 자리(x,z)에 직접 놓는다. 픽스처 — qaOps에 센다. 던전 판이 아니면 false
      namedDrop: (id: string, x?: number, z?: number) => { const h = game.view.hero; const ok = game.loot.spawnNamed(x ?? h.x + 3, z ?? h.z, id); game.qaOps++; return ok; },
      // 전직 관문·시련 입장(게임 명령 그대로 — 교관 UI 없이 실키 플레이 기록을 남길 때 쓴다. 픽스처가 아니라 qaOps에 세지 않는다)
      enterTrial: (id: string) => game.command({ type: 'enterTrial', trial: id }),
      // 장비 장부 사본(읽기 전용, 강화 장부 재로드 검증용)
      gear: () => game.gear.snapshot(),
      // 스테이지 진행 사본(D3a, 읽기 전용)
      progress: () => game.progress.snapshot(),
      // MMO 공유 필드 상태(읽기 전용)
      mmo: () => { const n = fieldLink.net; return n ? { status: n.status, online: n.online, you: n.you, channel: n.channel, count: n.count, peers: n.peerCount, visible: n.visibleCount, snaps: n.snaps, sentPos: n.sentPos, hits: fieldLink.hits, boss: { phase: n.boss.phase, hp: n.boss.hp, maxHp: n.boss.maxHp } } : null; },
      // 파티 판(레인 D) QA 훅: 파티 창(UI 레인)이 붙기 전에도 연결층을 직접 조작한다(로비 조작만 — 전투 입력은 키보드). ?partyws=로 서버 주소
      // 레이드 4인 넷 판 QA 훅: 지휘소 창이 없어도 연결층을 직접 조작한다(로비 조작만 — 전투 입력은 키보드). ?raidws=로 서버 주소
      raidnet: {
        create: (diff: 'normal' | 'hard' = 'normal') => { raidClientOf().create(diff); },
        join: (code: string) => { raidClientOf().join(code); },
        ready: (on = true) => game.raidNet.client?.ready(on),
        start: () => game.raidNet.client?.start(),
        leave: () => game.raidNet.client?.leave(),
        state: () => {
          const c = game.raidNet.client, v = game.view.raidNet;
          const b = game.view.boss;
          return c ? { status: c.status, code: c.raidCode, you: c.you, phase: c.room?.phase ?? '', members: c.room?.members.length ?? 0, inRun: c.inRun, error: c.error, mercs: c.mercs.slice(),
            active: v?.active ?? false, waiting: v?.waiting ?? false, scene: game.view.flow.scene,
            bossHp: b.hp, bossMaxHp: b.maxHp, phase2: b.phase2, bossState: b.state, serverHp: c.bossInfo.hp, serverTick: Math.round(c.serverTick()),
            slots: c.slots.size, teles: v?.teles ?? 0, heroHp: game.view.hero.hp, downs: v?.downs ?? 0, reviveTicks: v?.reviveTicks ?? 0,
            hitsSent: v?.hitsSent ?? 0, hurtTaken: v?.hurtTaken ?? 0, posSent: c.posSent, peers: v ? v.peers.filter((p) => p.active).length : 0,
            end: v?.result ? { ...v.result } : null } : null;
        },
      },
      party: {
        open: (name: string, code?: string) => {
          let c = game.party.client;
          if (!c) { c = new PartyClient({ name, get hero() { return game.view.heroId ?? 'kael'; }, get lv() { return game.growth.level; }, url: partyWsOverride() }); game.party.attach(c); }
          if (code) c.join(code); else c.create();
        },
        ready: (on = true) => game.party.client?.ready(on),
        start: () => game.party.client?.start(),
        leave: () => game.party.client?.leave(),
        state: () => {
          const c = game.party.client, v = game.view.party;
          return c ? { status: c.status, code: c.partyCode, you: c.you, phase: c.room?.phase ?? '', members: c.room?.members.length ?? 0, inRun: c.inRun, error: c.error, mobs: c.mobs.size, peers: c.peers.size,
            active: v?.active ?? false, wave: v?.wave ?? 0, waves: v?.waves ?? 0, spawned: v?.spawned ?? 0, killed: v?.killed ?? 0, hitsSent: v?.hitsSent ?? 0, hurtTaken: v?.hurtTaken ?? 0, reviveTicks: v?.reviveTicks ?? 0,
            result: v?.result ? { ...v.result } : null, scene: game.view.flow.scene, peerSlots: v ? v.peers.filter((p) => p.active).length : 0 } : null;
        },
        // 우편 적용(받기 → 적용 → ack 중 적용 단계, src/game/mailApply.ts)
        applyMail: (mail: MailLike) => applyMail(game, mail),
      },
    };
  }
  runLoop(loop);
  // 계정·서버 저장(D5a): 게임 루프 밖에서 세션 확인·주기 동기화. 실패는 설정 창 상태 줄에만 남고 진행을 막지 않는다.
  // 서버 진행 불러오기(새로고침)는 타이틀·마을에서만, 그 전에 미저장 진행을 쓴다
  // 이관 직후 이름 없는 캐릭터: 인트로가 걷힌 뒤 타이틀에서 선택 화면을 한 번 연다(이름 정하기 안내 — 닫고 나중에 정해도 된다)
  if (charEntry?.unnamed()) void (introDone() ?? Promise.resolve()).then(() => { if (game.view.flow.scene === Scene.Title) charEntry.open(NAME_NOTICE); });
  startAccount({
    isSafe: () => (game.view.flow.scene === Scene.Title || game.view.flow.scene === Scene.Village) && !charEntry?.isOpen(),
    flush: saveRunProgress,
    reload: () => location.reload(),
    setBusy: setBusyVeil, // 캐릭터 이관 중 짧은 입력 차단(CHARACTER-PRD §13.6)
  });
  // 커뮤니티·고객센터(D5b): 마을 가장자리 버튼 · 던전에서는 가장자리 띠만. 계정 세션이 준비되면 연결한다
  startCommunity({ scene: () => sceneName(), renderer: () => renderer.backend });
  // 자산 패치·PWA: 서비스 워커 등록(인트로가 걷힌 뒤)과 지역 데이터 미리 받기. 둘 다 표가 있는 빌드에서만 일하고, 저장·계정 동기화는 건드리지 않는다
  startRegionData({
    scene: () => sceneName(),
    unlocked: () => REGION_IDS.filter((r) => r === 'r1' || game.view.progress?.regionUnlocked(r) === true),
  });
  startPwa({
    introDone: introDone(),
    isSafe: () => game.view.flow.scene === Scene.Title || game.view.flow.scene === Scene.Village,
    flush: saveRunProgress,
  });
}

boot().catch(fail);
